import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { useAuth } from "@/lib/auth";
import { colors, radius, spacing, typography } from "@/lib/theme";
import {
    listTrainingBundles,
    type BundleProgress,
    type BundleWindow,
    type TrainingBundle
} from "@/lib/training-api";

type Row = { bundle: TrainingBundle; progress: BundleProgress; window?: BundleWindow | null };

/**
 * The nurse's training list — one card per bundle.
 *
 * Tapping a card opens that bundle's course list at /training/<slug>. This
 * screen deliberately knows no slugs: bundles come from the API, so publishing
 * a new one needs no app release.
 *
 * The shell mirrors app/compliance/index.tsx — same SafeAreaView edges, the
 * same back-arrow top bar, the same display/subheader pairing.
 */
export default function TrainingBundlesScreen() {
    const router = useRouter();
    const { user } = useAuth();
    const rnId = user?.rnId ?? null;

    const [rows, setRows] = useState<Row[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!rnId) {
            return;
        }
        setError(null);
        try {
            const r = await listTrainingBundles(rnId);
            setRows(r.bundles);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Couldn't load your training");
        } finally {
            setLoading(false);
        }
    }, [rnId]);

    // `rnId` starts null while auth rehydrates from storage, so this fires
    // again once it lands.
    useEffect(() => {
        void load();
    }, [load]);

    // Refresh on focus so returning from a finished course shows it ticked.
    useFocusEffect(
        useCallback(() => {
            void load();
        }, [load])
    );

    return (
        <SafeAreaView edges={["top", "bottom"]} style={styles.container}>
            <View style={styles.topBar}>
                <TouchableOpacity
                    style={styles.backArrow}
                    hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                    onPress={() => {
                        if (router.canGoBack()) {
                            router.back();
                        } else {
                            router.replace("/(tabs)");
                        }
                    }}
                >
                    <MaterialCommunityIcons
                        name="chevron-left"
                        size={28}
                        color={colors.textPrimary}
                    />
                </TouchableOpacity>
            </View>

            <ScrollView contentContainerStyle={styles.scroll}>
                <Text style={styles.header}>Training</Text>
                <Text style={styles.subheader}>Course bundles you complete in the app.</Text>

                {loading && (
                    <View style={styles.center}>
                        <ActivityIndicator size="large" color={colors.teal400} />
                    </View>
                )}

                {error && !loading && (
                    <View style={styles.center}>
                        <MaterialCommunityIcons
                            name="alert-circle-outline"
                            size={44}
                            color={colors.error500}
                        />
                        <Text style={styles.errorText}>{error}</Text>
                        <TouchableOpacity onPress={() => void load()}>
                            <Text style={styles.retry}>Try again</Text>
                        </TouchableOpacity>
                    </View>
                )}

                {!loading && !error && rows.length === 0 && (
                    <View style={styles.center}>
                        <Text style={styles.errorText}>No training assigned yet.</Text>
                    </View>
                )}

                {!loading &&
                    !error &&
                    rows.map(({ bundle, progress, window: renewal }) => (
                        <BundleCard
                            key={bundle.slug}
                            bundle={bundle}
                            progress={progress}
                            dueDate={renewal?.dueDate ?? null}
                            onPress={() => router.push(`/training/${bundle.slug}`)}
                        />
                    ))}
            </ScrollView>
        </SafeAreaView>
    );
}

function BundleCard({
    bundle,
    progress,
    dueDate,
    onPress
}: {
    bundle: TrainingBundle;
    progress: BundleProgress;
    /** Yearly bundles only: the current record's last valid day. */
    dueDate: string | null;
    onPress: () => void;
}) {
    const pct = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;
    const label = progress.isComplete ? "Review" : progress.completed > 0 ? "Continue" : "Start";

    return (
        <TouchableOpacity style={styles.hero} activeOpacity={0.9} onPress={onPress}>
            <Text style={styles.heroTitle}>{bundle.title}</Text>
            <Text style={styles.heroMeta}>
                {progress.completed} of {progress.total}{" "}
                {progress.total === 1 ? "course" : "courses"} complete
            </Text>
            {dueDate && !progress.isComplete ? (
                <Text style={styles.heroMeta}>
                    {`Yearly requirement · complete by ${new Date(
                        `${dueDate}T00:00:00Z`
                    ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        timeZone: "UTC"
                    })}`}
                </Text>
            ) : null}

            <View style={styles.bar}>
                <View style={[styles.barFill, { width: `${pct}%` }]} />
            </View>

            {progress.isComplete ? (
                <View style={styles.doneRow}>
                    <MaterialCommunityIcons name="check-circle" size={18} color={colors.teal400} />
                    <Text style={styles.doneText}>Complete</Text>
                </View>
            ) : null}

            <View style={styles.cta}>
                <Text style={styles.ctaText}>{label}</Text>
                <MaterialCommunityIcons name="arrow-right" size={18} color="#FFFFFF" />
            </View>
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    topBar: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: spacing.sm,
        paddingTop: spacing.xs
    },
    backArrow: {
        width: 44,
        height: 44,
        alignItems: "center",
        justifyContent: "center"
    },
    scroll: {
        paddingHorizontal: spacing.screenPadding,
        paddingBottom: spacing["3xl"],
        gap: spacing.md
    },
    header: {
        ...typography.display,
        color: colors.textPrimary
    },
    subheader: {
        ...typography.body,
        color: colors.textSecondary,
        marginBottom: spacing.sm
    },
    center: {
        alignItems: "center",
        paddingVertical: spacing["3xl"],
        gap: spacing.sm
    },
    errorText: {
        ...typography.body,
        color: colors.textSecondary,
        textAlign: "center"
    },
    retry: {
        ...typography.body,
        color: colors.teal600,
        fontWeight: "700"
    },

    hero: {
        backgroundColor: colors.teal700,
        borderRadius: radius.lg,
        padding: spacing.lg,
        gap: spacing.sm
    },
    heroTitle: {
        ...typography.h2,
        color: "#FFFFFF"
    },
    heroMeta: {
        ...typography.bodySmall,
        color: colors.teal100
    },
    bar: {
        height: 8,
        borderRadius: 4,
        backgroundColor: "rgba(255,255,255,0.22)",
        overflow: "hidden"
    },
    barFill: {
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.teal400
    },
    doneRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: spacing.xs
    },
    doneText: {
        ...typography.title,
        color: "#FFFFFF"
    },
    cta: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        backgroundColor: colors.teal500,
        borderRadius: radius.md,
        paddingVertical: 13,
        marginTop: spacing.xs
    },
    ctaText: {
        ...typography.title,
        color: "#FFFFFF"
    }
});
